/**
 *
 * @module paywallClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/paywall.md
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/paywall.md
 *
 */

/* eslint-disable camelcase */

import Check from 'check-types';

import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import AccountEntitlementContext from './accountEntitlementContext';
import Campaign from './campaign';
import IntroPrice from './introPrice';
import Paywall from './paywall';
import PaywallClientConfiguration from './paywallClientConfiguration';
import PaywallClientEndpoint from './paywallClientEndpoint';
import Product from './product';
import ProductType from './productType';
import TimeUnit from './timeUnit';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const PaywallClientDustUrnRefernce = DustUrnReference.services.paywall.paywallClient;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class PaywallClient
 * @desc Provides a data client that can be used to access paywall services.
 *
 */
export default class PaywallClient {

    /**
     *
     * @param {SDK.Services.Paywall.PaywallClientConfiguration} paywallClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(paywallClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: `${this.constructor.name}(paywallClientConfiguration, logger, httpClient)`,
            get paywallClientConfiguration() {
                return `${this.ctor} needs PaywallClientConfiguration instance`;
            }
        };

        instanceStrict(paywallClientConfiguration, PaywallClientConfiguration, errorMessage.paywallClientConfiguration, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Paywall.PaywallClientConfiguration}
         *
         */
        this.config = paywallClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc determines if you should refresh the access token
         *
         */
        this.refreshAccessTokenHeader = 'X-BAMTech-Refresh-Access-Token';

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(PaywallClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {String} accessToken
     * @desc Retrieves paywall information contextually sensitive to the environment and requester.
     * @returns {Promise<SDK.Services.Paywall.Paywall>}
     *
     */
    getPaywall(accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.getPaywall(accessToken)`,
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient, processProducts } = this;

        const payload = this.getPayload(
            accessToken,
            PaywallClientEndpoint.paywall
        );

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PaywallClient.name,
            urn: PaywallClientDustUrnRefernce.getPaywall,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { paywallHash, skus, context } = data;

            const accountEntitlementContext = AccountEntitlementContext[context];
            const products = processProducts(skus);

            const paywall = new Paywall({
                accountEntitlementContext,
                paywallHash,
                products
            });

            return Promise.resolve(paywall);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Array<Object>} options
     * @returns {Array<SDK.Services.Paywall.Product>}
     *
     */
    processProducts(options) {

        const products = [];

        for (const item of options) {

            let _campaign;
            let _introPrice = null;

            const { entitlements, introPrice, name, groups, campaign, sku, productType } = item;
            const { campaignCode, voucherCode } = campaign;
            const { unit, price, length } = introPrice;

            if (Check.assigned(campaignCode) && Check.assigned(voucherCode)) {
                _campaign = new Campaign(campaignCode, voucherCode);
            }

            if (Check.assigned(unit) && Check.assigned(price) && Check.assigned(length)) {

                _introPrice = new IntroPrice({
                    length,
                    price,
                    unit: TimeUnit[unit]
                });

            }

            const product = new Product({
                campaign: _campaign,
                entitlements,
                groups,
                introPrice: _introPrice,
                name,
                productType: ProductType[productType],
                sku
            });

            products.push(product);

        }

        return products;

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {String} rel
     * @param {Object} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/paywall/paywallClient.js